import request from '../../../util/SuperagentUtils';
import {
  adminBookingsReportEndpoints,
  buyerBookingsReportEndpoints,
  supplierBookingsReportEndpoints,
} from '../../../requests/api-endpoints';
import type { AssistantAudience } from '../assistantCapabilities';

/**
 * Looks a booking up by its order number, for the payment-failure skill.
 *
 * The booking a user names ("booking 1021445") is the *order number*, not the
 * internal id the by-id endpoints take, so a lookup has to go through search.
 * Every audience's booking search accepts a `bookingNumber` filter and scopes
 * the rest to the caller's token — a buyer sees only their bookings, a supplier
 * only theirs — so the endpoint is chosen from the audience and nothing else is
 * sent. The admin search is account-wide, which is correct for staff.
 *
 * `size` is small on purpose: an order number is unique, so a match is one row,
 * and a page of one keeps a mistyped number from dragging back the whole report.
 */

/** One booking row. Loose and local — only the fields the skill reads. */
export interface BookingLookupRow {
  orderNumber?: string;
  status?: string;
  locationName?: string;
  supplierCompanyName?: string;
  buyerCompanyName?: string;
  numberOfSpaces?: number;
  startDate?: string;
  endDate?: string;
  creationDate?: string;
  recurringCharge?: number;
  initialCharge?: number;
}

const SEARCH_ENDPOINT: Record<AssistantAudience, string> = {
  buyer: buyerBookingsReportEndpoints.search,
  supplier: supplierBookingsReportEndpoints.search,
  admin: adminBookingsReportEndpoints.search,
};

export const searchBookingByNumber = (
  audience: AssistantAudience,
  bookingNumber: string,
): Promise<{ body?: { content?: BookingLookupRow[]; count?: number } }> => {
  const params = new URLSearchParams({
    bookingNumber,
    page: '0',
    size: '1',
  });
  return request.get(`${SEARCH_ENDPOINT[audience]}?${params.toString()}`);
};
